import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { ApiError, api } from '../api/client';
import type { Plan, Subscription } from '../api/types';
import { Alert, LoadingBlock } from '../components/ui';
import { useAuth } from '../context/AuthContext';

/**
 * Retursiden etter betaling.
 *
 * Testbetalingen bekreftes her, og det er det som gjør hele oppgraderingsflyten
 * prøvbar uten nøkler. Ekte betalinger blir aldri låst opp herfra: det er
 * webhooken som aktiverer pakken, for denne adressen kan hvem som helst skrive
 * inn i adressefeltet.
 *
 * Derfor må siden vente. Stripe sender nettleseren tilbake med én gang, og
 * webhooken kommer sin egen vei – som regel på et sekund eller to, men ikke
 * alltid før paret er tilbake. Uten ventingen ville de fått «Gratis er låst
 * opp» på skjermen rett etter å ha betalt.
 */

/** Tjue forsøk med halvannet sekund mellom: tretti sekunder. */
const FORSOK = 20;
const PAUSE_MS = 1500;

type Tilstand = 'venter' | 'ferdig' | 'treg' | 'feil';

const pause = (ms: number) => new Promise((ferdig) => setTimeout(ferdig, ms));

export function PaymentReturnPage() {
  const [params] = useSearchParams();
  const { setPlan, refresh } = useAuth();
  const [tilstand, setTilstand] = useState<Tilstand>('venter');
  const [melding, setMelding] = useState<string | null>(null);
  const [abonnement, setAbonnement] = useState<Subscription | null>(null);
  const startet = useRef(false);
  const paaSkjermen = useRef(true);

  const reference = params.get('ref');
  const planParam = params.get('plan') as Plan | null;
  const weddingId = params.get('wedding');

  useEffect(() => {
    paaSkjermen.current = true;
    return () => {
      paaSkjermen.current = false;
    };
  }, []);

  /**
   * Er pakken de nettopp kjøpte, den de har nå?
   *
   * Ikke «har de noe annet enn gratis»: et par som oppgraderer fra Standard til
   * Plus, har alt noe annet enn gratis. Da ville siden sagt seg ferdig før
   * betalingen var bekreftet.
   */
  const erFramme = useCallback(
    (abo: Subscription) =>
      abo.status === 'active' && (planParam ? abo.plan === planParam : abo.plan !== 'free'),
    [planParam],
  );

  const gjennomfor = useCallback(async () => {
    if (!weddingId) {
      setTilstand('feil');
      setMelding('Mangler informasjon om hvilket bryllup betalingen gjelder.');
      return;
    }

    setTilstand('venter');
    setMelding(null);

    try {
      // Testbetalingen har ingen leverandør som kan bekrefte noe. Den bekreftes
      // her, og bare når miljøet tillater det – API-et avviser den ellers.
      if (reference?.startsWith('mock_') && planParam) {
        const svar = await api.post<{ subscription: Subscription }>(
          `/weddings/${weddingId}/billing/confirm`,
          { reference, plan: planParam },
        );
        setAbonnement(svar.subscription);
        setPlan(weddingId, svar.subscription.plan);
        await refresh();
        if (paaSkjermen.current) setTilstand('ferdig');
        return;
      }

      for (let runde = 0; runde < FORSOK; runde += 1) {
        const svar = await api.get<{ subscription: Subscription }>(
          `/weddings/${weddingId}/billing`,
        );
        if (!paaSkjermen.current) return;
        setAbonnement(svar.subscription);

        if (erFramme(svar.subscription)) {
          setPlan(weddingId, svar.subscription.plan);
          await refresh();
          if (paaSkjermen.current) setTilstand('ferdig');
          return;
        }
        await pause(PAUSE_MS);
        if (!paaSkjermen.current) return;
      }

      // Ikke en feil. Pengene er trukket, og bekreftelsen kommer – den har bare
      // ikke kommet ennå. Å kalle det «betalingen ble ikke fullført» ville sendt
      // et par som nettopp har betalt, til å betale en gang til.
      setTilstand('treg');
    } catch (feilet) {
      if (!paaSkjermen.current) return;
      setTilstand('feil');
      setMelding(
        feilet instanceof ApiError ? feilet.message : 'Klarte ikke å bekrefte betalingen.',
      );
    }
  }, [erFramme, planParam, reference, refresh, setPlan, weddingId]);

  useEffect(() => {
    if (startet.current) return;
    startet.current = true;
    void gjennomfor();
  }, [gjennomfor]);

  if (tilstand === 'venter') return <LoadingBlock label="Bekrefter betalingen …" />;

  return (
    <div className="onboarding">
      <div className="onboarding-card center">
        {tilstand === 'ferdig' ? (
          <>
            <span style={{ fontSize: '2.6rem', display: 'block' }} aria-hidden="true">
              🎉
            </span>
            <h1>{abonnement?.planName} er låst opp</h1>
            <p className="lead">
              Alt dere har lagt inn er der fortsatt – nå med flere verktøy tilgjengelig.
            </p>
            <Link className="btn btn-primary btn-lg" to="/app">
              Tilbake til oversikten
            </Link>
          </>
        ) : tilstand === 'treg' ? (
          <>
            <h1>Betalingen er registrert</h1>
            <Alert kind="info">
              Bekreftelsen fra betalingsleverandøren har ikke kommet fram til oss ennå. Den
              pleier å ta noen sekunder. Dere trenger ikke betale på nytt – pakken låses opp av
              seg selv så snart bekreftelsen er her, og kvitteringen kommer på e-post.
            </Alert>
            <div className="modal-actions" style={{ justifyContent: 'center' }}>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => void gjennomfor()}
              >
                Sjekk igjen
              </button>
              <Link className="btn btn-ghost" to="/app">
                Tilbake til oversikten
              </Link>
            </div>
          </>
        ) : (
          <>
            <h1>Betalingen ble ikke fullført</h1>
            <Alert>{melding}</Alert>
            <Link className="btn btn-secondary" to="/app/abonnement">
              Prøv igjen
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
